import { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  modalErrorMessage,
  toastErrorMessage,
} from "../../utils/toastMessage/toastMessage";
import {
  editInfoService,
  sendOTPService,
  signInService,
  verifyOTPService,
} from "./../../services/userServices";
import { signInOrganService } from "./../../services/organService";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../redux/actions/loadingActions/loadingActions";
import { toastSuccessMessage } from "./../../utils/toastMessage/toastMessage";
import { baseRoute, isValidPhoneNumber, setCookies } from "../../utils";

export const authContext = createContext({
  username: "",
  setUsername: () => {},
  password: "",
  setPassword: () => {},
  phoneNumber: "",
  setPhoneNumber: () => {},
  isSendCode: null,
  setIsSendCode: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  firstName: "",
  setFirstName: () => {},
  lastName: "",
  setLastName: () => {},
  handleSignIn: () => {},
  handleSendOTP: () => {},
  handleVerifyOTP: () => {},
  handleReset: () => {},
  handleOpenModal: () => {},
  handleCloseModal: () => {},
  handleEditInfo: () => {},
});

const AuthContext = ({ isOrgan, children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [token, setToken] = useState("");
  const [isSendCode, setIsSendCode] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);

  // Handle Open Modal
  const handleOpenModal = () => setIsShowModal(true);
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleReset();
  };

  // Handle Reset State Method
  const handleReset = () => {
    setPhoneNumber("");
    setVerificationCode("");
    setToken("");
    setFirstName("");
    setLastName("");
    setIsSendCode(false);
  };

  // Handle Sign In Method
  const handleSignIn = async (event) => {
    event.preventDefault();

    const objData = {
      mobile: username,
      password,
    };

    if (!username) return toastErrorMessage("نام کاربری الزامی میباشد");
    if (!password) return toastErrorMessage("رمزعبور الزامی میباشد");

    const api = isOrgan ? signInOrganService : signInService;

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await api(objData);
      if (status === 200) {
        setCookies("accessToken", data.token);
        localStorage.setItem("role", data.permission_level);
        window.location.href = baseRoute(
          isOrgan,
          `/${isOrgan ? "Organ" : "Admin"}/Dashboard`
        );
      }
    } catch ({ response }) {
      if (response?.status) {
        modalErrorMessage("متاسفانه خطایی رخ داده است.");
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Send OTP Method
  const handleSendOTP = async (event) => {
    event.preventDefault();

    const objData = {
      mobile: phoneNumber,
      console: 1,
    };

    if (!isValidPhoneNumber(phoneNumber))
      return toastErrorMessage("شماره موبایل معتبر نیست");

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await sendOTPService(objData);
      if (status === 200) {
        toastSuccessMessage(`کد تایید به شماره موبایل ${phoneNumber} ارسال شد`);
        setIsSendCode(true);
      }
    } catch ({ response }) {
      if (response?.status) {
        modalErrorMessage(
          "متاسفانه خطایی رخ داده است. لطفا پس از چک کردن مجدد شماره تلفن تلاش نمایید."
        );
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Verify OTP Method
  const handleVerifyOTP = async (event) => {
    event.preventDefault();

    const objData = {
      token: verificationCode,
    };

    if (!verificationCode) return toastErrorMessage("کد تایید الزامی میباشد");

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await verifyOTPService(objData);
      if (status === 200) {
        if (isEmpty(data.name) || data.name === " ") {
          handleOpenModal();
          setToken(data.token);
        } else {
          setCookies("accessToken", data.token);
          window.location.href = baseRoute(
            isOrgan,
            `/${isOrgan ? "Organ" : "Admin"}/Dashboard`
          );
        }
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage(
          "کد وارد شده صحیح نیست یا مدت اعتبار کد تمام شده‌است."
        );
        // handleReset();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Info Method
  const handleEditInfo = async (event) => {
    event.preventDefault();

    const objData = {
      first_name: firstName,
      last_name: lastName,
    };

    if (firstName.length < 3)
      return toastErrorMessage("نام نمیتواند کمتر از 3 کاراکتر باشد");
    if (lastName.length < 3)
      return toastErrorMessage("نام خانوادگی نمیتواند کمتر از 3 کاراکتر باشد");

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editInfoService(objData, token);
      if (status === 200) {
        setCookies("accessToken", token);
        handleCloseModal();
        window.location.href = baseRoute(
          isOrgan,
          `/${isOrgan ? "Organ" : "Admin"}/Dashboard`
        );
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <authContext.Provider
      value={{
        username,
        setUsername,
        password,
        setPassword,
        phoneNumber,
        setPhoneNumber,
        verificationCode,
        setVerificationCode,
        isSendCode,
        setIsSendCode,
        isShowModal,
        setIsShowModal,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        handleSignIn,
        handleSendOTP,
        handleVerifyOTP,
        handleReset,
        handleOpenModal,
        handleCloseModal,
        handleEditInfo,
      }}
    >
      {children}
    </authContext.Provider>
  );
};

export default AuthContext;
